import { useState, useEffect } from "react";
import {
  Sheet,
  SheetContent,
  SheetHeader,
  SheetTitle,
  SheetDescription,
  SheetFooter,
} from "@/components/ui/sheet";
import { Button } from "@/components/ui/button";
import { TruncatedText } from "@/components/ui/truncated-text";
import type { ABTest } from "@/types/ab-tests";
import { getContentTypeLabel } from "@/utils/ab-test-utils";
import { ActivityLogTimeline } from "./ActivityLogTimeline";
import { EditResultsActionSection } from "./EditResultsActionSection";

interface EditResultsDrawerProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  test: ABTest;
  productId: number | string | null;
  brandId: number | null;
  onReuploadResults?: () => void;
  onSavePatch?: (testId: string, patch: Record<string, unknown>) => void;
}

export function EditResultsDrawer({
  open,
  onOpenChange,
  test,
  productId,
  brandId,
  onReuploadResults,
  onSavePatch,
}: EditResultsDrawerProps) {
  const [pendingOutcome, setPendingOutcome] = useState<string | undefined>(
    test.outcome ?? undefined,
  );
  const [pendingSwitchDate, setPendingSwitchDate] = useState<string | undefined>(
    test.variantBSwitchDate ?? undefined,
  );

  const testOutcome = test.outcome;
  const testVarBSwitchDate = test.variantBSwitchDate;

  useEffect(() => {
    setPendingOutcome(testOutcome ?? undefined);
    setPendingSwitchDate(testVarBSwitchDate ?? undefined);
  }, [test.id, open, testOutcome, testVarBSwitchDate]);

  const handleActionChange = (outcome: string, switchDate?: string) => {
    setPendingOutcome(outcome);
    setPendingSwitchDate(switchDate);
  };

  const handleSave = () => {
    if (onSavePatch) {
      onSavePatch(test.id, {
        outcome: pendingOutcome,
        variant_b_switch_date: pendingSwitchDate,
      });
    }
    onOpenChange(false);
  };

  const scopeFields = test.scopeFields || [test.contentType];

  const metricRows = test.amazonMetrics
    ? [
        { label: "Conversion", data: test.amazonMetrics.conversion },
        {
          label: "Units Sold",
          data: test.amazonMetrics.unitsSold
            ? {
                a: test.amazonMetrics.unitsSold.a.toLocaleString(),
                b: test.amazonMetrics.unitsSold.b.toLocaleString(),
                diff: test.amazonMetrics.unitsSold.diff.toLocaleString(),
              }
            : undefined,
        },
        { label: "Sales", data: test.amazonMetrics.sales },
        {
          label: "Sample Size",
          data: test.amazonMetrics.sampleSize
            ? {
                a: test.amazonMetrics.sampleSize.a.toLocaleString(),
                b: test.amazonMetrics.sampleSize.b.toLocaleString(),
                diff: test.amazonMetrics.sampleSize.diff.toLocaleString(),
              }
            : undefined,
        },
      ].filter((row) => row.data)
    : [];

  return (
    <Sheet open={open} onOpenChange={onOpenChange}>
      <SheetContent side="right" className="w-full sm:max-w-[640px] bg-card p-0 flex flex-col gap-0">
        <SheetHeader className="px-6 pt-6 pb-4 border-b border-border space-y-1">
          <SheetTitle>Edit Results & Action</SheetTitle>
          <SheetDescription>
            The test configuration is locked. You can update the results and action.
          </SheetDescription>
        </SheetHeader>

        <div className="flex-1 overflow-y-auto px-6 py-4">
          <div className="space-y-4">
            <div className="grid grid-cols-3 gap-4 bg-muted/50 rounded-md p-3.5">
              <div className="space-y-1">
                <p className="text-[10px] uppercase tracking-wider text-muted-foreground font-medium">Created by</p>
                <p className="text-sm text-foreground font-semibold">
                  <TruncatedText>{test.createdBy ?? "—"}</TruncatedText>
                </p>
              </div>
              <div className="space-y-1">
                <p className="text-[10px] uppercase tracking-wider text-muted-foreground font-medium">Started</p>
                <p className="text-sm text-foreground font-semibold">{test.startDate ?? "—"}</p>
              </div>
              <div className="space-y-1">
                <p className="text-[10px] uppercase tracking-wider text-muted-foreground font-medium">Stopped</p>
                <p className="text-sm text-foreground font-semibold">{test.endDate ?? "—"}</p>
              </div>
            </div>

            <div className="grid grid-cols-2 gap-4 bg-muted/50 rounded-md p-3.5">
              <div className="space-y-1">
                <p className="text-[10px] uppercase tracking-wider text-muted-foreground font-medium">Test Type</p>
                <p className="text-sm text-foreground font-semibold capitalize">{test.testType}</p>
              </div>
              <div className="space-y-1">
                <p className="text-[10px] uppercase tracking-wider text-muted-foreground font-medium">Scope</p>
                <p className="text-sm text-foreground font-semibold">
                  {scopeFields.map((f) => getContentTypeLabel(f)).join(", ")}
                </p>
              </div>
            </div>

            {test.hypothesis && (
              <div className="space-y-1.5 bg-muted/50 rounded-md p-3.5">
                <p className="text-[10px] uppercase tracking-wider text-muted-foreground font-medium">Hypothesis</p>
                <p className="text-sm leading-relaxed text-foreground">{test.hypothesis}</p>
              </div>
            )}

            <div className="space-y-2.5">
              <div className="flex items-center justify-between">
                <p className="text-[10px] uppercase tracking-wider text-muted-foreground font-medium">
                  Amazon Experiment Results
                </p>
                {onReuploadResults && (
                  <Button
                    variant="ghost"
                    size="sm"
                    className="h-6 text-xs"
                    onClick={() => {
                      onOpenChange(false);
                      onReuploadResults();
                    }}
                  >
                    Re-upload
                  </Button>
                )}
              </div>
              {test.amazonMetrics && metricRows.length > 0 ? (
                <div className="border border-border rounded-md overflow-hidden">
                  <div className="grid grid-cols-4 bg-muted/50 border-b border-border">
                    <div className="py-2 px-3 text-[10px] uppercase tracking-wider text-muted-foreground font-medium">Metric</div>
                    <div className="py-2 px-3 text-[10px] uppercase tracking-wider text-muted-foreground font-medium text-right">Variant A</div>
                    <div className="py-2 px-3 text-[10px] uppercase tracking-wider text-muted-foreground font-medium text-right">Variant B</div>
                    <div className="py-2 px-3 text-[10px] uppercase tracking-wider text-muted-foreground font-medium text-right">Difference</div>
                  </div>
                  {metricRows.map((row) => (
                    <div key={row.label} className="grid grid-cols-4 border-b border-border/50 last:border-b-0">
                      <div className="py-2 px-3 text-xs text-foreground font-medium">{row.label}</div>
                      <div className="py-2 px-3 text-xs text-foreground text-right tabular-nums">{row.data!.a}</div>
                      <div className="py-2 px-3 text-xs text-foreground text-right tabular-nums">{row.data!.b}</div>
                      <div className="py-2 px-3 text-xs text-foreground text-right tabular-nums">{row.data!.diff}</div>
                    </div>
                  ))}
                </div>
              ) : (
                <p className="text-sm text-muted-foreground italic">No metrics uploaded yet.</p>
              )}
            </div>

            <EditResultsActionSection
              outcome={test.outcome}
              variantBSwitchDate={test.variantBSwitchDate}
              onChange={handleActionChange}
            />

            <div className="pt-2 border-t border-border">
              <div className="pt-4">
                <ActivityLogTimeline
                  entries={test.activityLog || []}
                  showHeader={true}
                  productId={typeof productId === "number" ? productId : (productId ? Number(productId) : undefined)}
                  brandId={brandId ?? undefined}
                  testId={test.id}
                />
              </div>
            </div>
          </div>
        </div>

        <SheetFooter className="px-6 py-4 border-t border-border bg-card">
          <Button variant="outline" onClick={() => onOpenChange(false)}>
            Cancel
          </Button>
          <Button onClick={handleSave}>Save Changes</Button>
        </SheetFooter>
      </SheetContent>
    </Sheet>
  );
}
